import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'

/**
 * PostingInsights Component
 * 
 * Shows demand metrics for a scrimmage posting based on saved searches
 * Displays how many teams might be interested in this time slot
 * 
 * Props:
 * - date: Scrimmage date (YYYY-MM-DD)
 * - level: Age group
 * - levelDetail: Tier (e.g., 'B1')
 * - postingType: 'available' or 'wanted'
 */

export default function PostingInsights({ date, level, levelDetail, postingType }) {
  const [insights, setInsights] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (date && level) {
      loadInsights()
    }
  }, [date, level, levelDetail, postingType])

  const loadInsights = async () => {
    setLoading(true)
    try {
      // Query saved searches that match this posting's criteria
      let query = supabase
        .from('scrimmage_searches')
        .select('id, search_name, team_id')
        .eq('alert_enabled', true)

      // Match level
      if (level) {
        query = query.or(`filter_level.is.null,filter_level.eq.${level}`)
      }

      // Match level detail
      if (levelDetail) {
        query = query.or(`filter_level_detail.is.null,filter_level_detail.eq.${levelDetail}`)
      }

      // Match date range
      if (date) {
        query = query.or(
          `filter_date_start.is.null,filter_date_start.lte.${date}`
        )
        query = query.or(
          `filter_date_end.is.null,filter_date_end.gte.${date}`
        )
      }

      // Match posting type (inverse: if looking for 'available', teams want 'wanted' postings)
      if (postingType === 'available') {
        query = query.or('filter_posting_type.is.null,filter_posting_type.eq.wanted')
      } else if (postingType === 'wanted') {
        query = query.or('filter_posting_type.is.null,filter_posting_type.eq.available')
      }

      const { data, error } = await query

      if (error) {
        console.error('[PostingInsights] Error loading insights:', error)
        setInsights(null)
        return
      }

      // Count unique teams (exclude duplicates)
      const uniqueTeams = new Set(data?.map(s => s.team_id) || [])
      const matchCount = uniqueTeams.size

      setInsights({
        matchCount,
        isHighDemand: matchCount >= 3
      })
    } catch (error) {
      console.error('[PostingInsights] Error:', error)
      setInsights(null)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-blue-800">Checking demand...</span>
        </div>
      </Card>
    )
  }

  if (!insights || insights.matchCount === 0) {
    return null
  }

  return (
    <Card className={`${insights.isHighDemand ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
      <div className="flex items-start gap-3">
        <div className="text-2xl">
          {insights.isHighDemand ? '🔥' : '📊'}
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-brand-charcoal mb-1">
            {insights.isHighDemand ? 'High Demand Time Slot!' : 'Teams Interested'}
          </h4>
          <p className="text-sm text-gray-700">
            <strong>{insights.matchCount}</strong> {insights.matchCount === 1 ? 'team has' : 'teams have'} saved searches that match this scrimmage
          </p>
          {insights.isHighDemand && (
            <p className="text-xs text-green-800 mt-1">
              This time slot is in high demand. You'll likely get responses quickly!
            </p>
          )}
        </div>
      </div>
    </Card>
  )
}

